import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, SectionCard } from '@/components/ui';
import { AccountsFacetRail } from './AccountsFacetRail';
import { AccountsToolbar } from './AccountsToolbar';
import type { FacetGroup } from './facetTypes';
import type { ScopeBarProps } from './ScopeBar';
import { ScopeBar } from './ScopeBar';

export type AccountsTab = 'accounts' | 'audit';

export interface AccountsScreenProps {
    context: 'realm' | 'org';
    title: string;
    subtitle: string;
    headerActions?: ReactNode;

    /** `null` hides the tab bar entirely (neither context does this today, but an
     *  adapter without audit-view capability may still want one tab only —
     *  see each page for how `canViewAudit` gates the second tab). */
    tab: AccountsTab;
    onTabChange: (tab: AccountsTab) => void;
    showAuditTab: boolean;
    auditPanel: ReactNode;

    /** Org-only. `undefined` on the realm screen — it has no branch to narrow. */
    scopeBar?: ScopeBarProps;


    facetGroups: FacetGroup[];
    onSelectFacet: (groupKey: string, optionKey: string) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;

    search: string;
    onSearchChange: (value: string) => void;
    searchDisabledReason?: string;
    activeFilterCount: number;
    rangeLabel: string;

    /** Rendered below the toolbar: bulk bar (if any selection) + table/pagination,
     *  OR a loading/error/empty state — each page owns which of those applies. */
    body: ReactNode;
}

/**
 * EAA-1820: the ONE accounts screen — replaces `AccountsPage.tsx` (realm) and
 * `ScopedAccountsPage.tsx` (org)'s duplicated layout. Per the design pack's own
 * README (the governing rule this ticket implements): "Danh sách tài khoản ở tổ
 * chức và ở realm dùng chung một UI: cùng tab, bộ lọc đã lưu, rail bộ lọc, bảng và
 * thanh thao tác. Khác nhau chỉ ở ngữ cảnh."
 *
 * What is genuinely context-specific stays OUTSIDE this component and is passed
 * in: the "Phạm vi" bar (org only, via `scopeBar`), the write dialogs (issue/
 * reset/delete for realm; add/revoke/reset + bulk Level-assign for org — neither
 * belongs to a shared screen since their backends and permissions differ
 * entirely), and the `body` slot's table columns/bulk actions (built from
 * `shared/AccountsTable` + `shared/AccountsBulkBar` + `shared/AccountsPagination`
 * by each page, since the two row shapes — a Keycloak account vs. an Assignment
 * grant — are different entities with different columns).
 *
 * FIT-TO-SHELL (frame 69): `portal-console-page--fit acc-screen` — title, tabs,
 * saved-filter chips, then a growing board (facet rail + table card). No outer
 * SectionCard titled "Danh sách tài khoản"; only `.acc-table-scroll` scrolls.
 */
export function AccountsScreen({
    title,
    subtitle,
    headerActions,
    tab,
    onTabChange,
    showAuditTab,
    auditPanel,
    scopeBar,
    facetGroups,
    onSelectFacet,
    hasActiveFilters,
    onClearFilters,
    search,
    onSearchChange,
    searchDisabledReason,
    activeFilterCount,
    rangeLabel,
    body,
}: AccountsScreenProps) {
    const { t } = useTranslation();

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit acc-screen">
            {/* EAA-1820 (frame 69): title/actions then tabs then filters then the
                growing board — no "Danh sách tài khoản" SectionCard wrapper;
                spacing comes from the page column gap (12px), not PageHeader's
                default 24px bottom margin. */}
            <PageHeader
                title={title}
                collapseSubtitle={false}
                subtitle={subtitle}
                actions={headerActions}
                marginBottom={0}
            />

            <div className="acc-tabs" role="tablist" aria-label={title}>
                <button
                    type="button"
                    role="tab"
                    id="acc-tab-list"
                    aria-selected={tab === 'accounts'}
                    aria-controls="acc-panel-list"
                    className={'acc-tab' + (tab === 'accounts' ? ' is-active' : '')}
                    onClick={() => onTabChange('accounts')}
                >
                    {t('console.accounts.page.title')}
                </button>
                {showAuditTab && (
                    <button
                        type="button"
                        role="tab"
                        id="acc-tab-audit"
                        aria-selected={tab === 'audit'}
                        aria-controls="acc-panel-audit"
                        className={'acc-tab' + (tab === 'audit' ? ' is-active' : '')}
                        onClick={() => onTabChange('audit')}
                    >
                        {t('console.accounts.page.tabAudit')}
                    </button>
                )}
            </div>

            {tab === 'audit' && showAuditTab && (
                <div className="fit-grow" role="tabpanel" id="acc-panel-audit" aria-labelledby="acc-tab-audit">
                    <SectionCard title={t('console.accounts.page.tabAudit')}>{auditPanel}</SectionCard>
                </div>
            )}

            {tab === 'accounts' && (
                <div role="tabpanel" id="acc-panel-list" aria-labelledby="acc-tab-list" className="acc-screen__list">
                    {scopeBar && <ScopeBar {...scopeBar} />}

                    <div className="fit-grow acc-screen__board">
                        <div className="acc-board">
                            <AccountsFacetRail
                                groups={facetGroups}
                                onSelect={onSelectFacet}
                                hasActiveFilters={hasActiveFilters}
                                onClear={onClearFilters}
                            />
                            <div className="acc-board__content">
                                <AccountsToolbar
                                    search={search}
                                    onSearchChange={onSearchChange}
                                    searchDisabledReason={searchDisabledReason}
                                    activeFilterCount={activeFilterCount}
                                    onClearFilters={onClearFilters}
                                    rangeLabel={rangeLabel}
                                />
                                <div className="acc-board__body">{body}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

export default AccountsScreen;
